import React, { createContext, useContext, useState, useCallback } from 'react';

interface Breadcrumb {
  title: string;
  path: string;
}

interface HeaderContextType {
  breadcrumbs: Breadcrumb[];
  addCrumb: (crumb: Breadcrumb) => void;
  clearCrumb: () => void;
  removeLastCrumb: () => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

export const useHeader = () => {
  const context = useContext(HeaderContext);
  if (context === undefined) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
};

interface HeaderProviderProps {
  children: React.ReactNode;
}

export const HeaderProvider: React.FC<HeaderProviderProps> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);

  const addCrumb = useCallback((crumb: Breadcrumb) => {
    setBreadcrumbs(prev => [...prev, crumb]);
  }, []);

  const clearCrumb = useCallback(() => {
    setBreadcrumbs([]);
  }, []);

  const removeLastCrumb = useCallback(() => {
    setBreadcrumbs(prev => prev.slice(0, -1));
  }, []);

  return (
    <HeaderContext.Provider value={{ breadcrumbs, addCrumb, clearCrumb, removeLastCrumb }}>
      {children}
    </HeaderContext.Provider>
  );
};
